import { ReactNode } from 'react';
import { ReactComponent as CheckIcon } from '../../../../assets/check2.svg';
import { ReactComponent as PlusIcon } from '../../../../assets/plus_2.svg';

export type SelectableOptionItem = {
  id: string;
  /** Primary text displayed for the option */
  label: string;
  /** Optional secondary text displayed below the label */
  sublabel?: string;
  /** Optional array of secondary texts displayed below the label (e.g., multiple phone numbers) */
  sublabels?: string[];
  /** Whether this option is selected by default */
  isDefault?: boolean;
  /** Optional custom icon to display */
  icon?: ReactNode;
};

export type SelectableOptionsProps = {
  /** List of selectable options */
  options: SelectableOptionItem[];
  /** Currently selected option ID */
  selectedId: string | null;
  /** Callback when an option is selected */
  onSelect: (id: string) => void;
  /** Whether to show the "Add new" option */
  showAddNew?: boolean;
  /** Label for "Add new" button */
  addNewLabel?: string;
  /** Whether the "Add new" option is currently selected */
  isAddNewSelected?: boolean;
  /** Callback when "Add new" is clicked */
  onAddNew?: () => void;
  /** Additional CSS class */
  className?: string;
  /** Whether the options are disabled */
  disabled?: boolean;
  /** Accessible label for the options group */
  ariaLabel?: string;
};

export const SelectableOptions = ({
  options,
  selectedId,
  onSelect,
  showAddNew = false,
  addNewLabel = 'Add new',
  isAddNewSelected = false,
  onAddNew,
  className = '',
  disabled = false,
  ariaLabel,
}: SelectableOptionsProps) => {
  return (
    <div
      role="group"
      aria-label={ariaLabel}
      className={`selectable-options ${className}${disabled ? ' selectable-options--disabled' : ''}`}
    >
      {options.map(option => {
        const isSelected = selectedId === option.id && !isAddNewSelected;
        return (
          <div
            key={option.id}
            role="button"
            tabIndex={disabled ? -1 : 0}
            className={`selectable-options__item ${isSelected ? 'selectable-options__item--selected' : ''}${disabled ? ' selectable-options__item--disabled' : ''}`}
            onClick={() => !disabled && onSelect(option.id)}
            onKeyDown={e => e.key === 'Enter' && !disabled && onSelect(option.id)}
            aria-pressed={isSelected}
            aria-disabled={disabled}
            aria-label={`${option.label}${option.sublabel ? ', ' + option.sublabel : ''}${option.sublabels ? ', ' + option.sublabels.join(', ') : ''}${isSelected ? ', selected' : ''}`}
          >
            {option.icon && (
              <span
                className="selectable-options__item-icon"
                aria-hidden="true"
              >
                {option.icon}
              </span>
            )}
            <div className="selectable-options__item-content">
              <span className="selectable-options__item-label">{option.label}</span>
              {option.sublabels && option.sublabels.length > 0
                ? option.sublabels.map((sublabel, idx) => (
                    <span
                      key={idx}
                      className="selectable-options__item-sublabel"
                    >
                      {sublabel}
                    </span>
                  ))
                : option.sublabel && <span className="selectable-options__item-sublabel">{option.sublabel}</span>}
            </div>
            <span
              className="selectable-options__item-check"
              aria-hidden="true"
            >
              {isSelected && <CheckIcon />}
            </span>
          </div>
        );
      })}

      {showAddNew && (
        <div
          role="button"
          tabIndex={disabled ? -1 : 0}
          className={`selectable-options__item selectable-options__item--add ${
            isAddNewSelected ? 'selectable-options__item--selected' : ''
          }${disabled ? ' selectable-options__item--disabled' : ''}`}
          onClick={() => !disabled && onAddNew?.()}
          onKeyDown={e => e.key === 'Enter' && !disabled && onAddNew?.()}
          aria-pressed={isAddNewSelected}
          aria-disabled={disabled}
        >
          <span
            className="selectable-options__item-icon selectable-options__item-icon--add"
            aria-hidden="true"
          >
            <PlusIcon />
          </span>
          <span className="selectable-options__item-label">{addNewLabel}</span>
          <span
            className="selectable-options__item-check"
            aria-hidden="true"
          >
            {isAddNewSelected && <CheckIcon />}
          </span>
        </div>
      )}
    </div>
  );
};
